import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import CheckIcon from "@mui/icons-material/Check";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { Box, Typography } from "@mui/material";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MetaChip } from "../../../../components/UIToolkit/Badges/MetaChip";
import { PersonActionRow } from "../../../../components/UIToolkit/Cells/PersonActionRow";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useFormatPersonName } from "../../../../hooks/useNameFormat";
import { ACCENT_BLUE, FONT_SIZE, INK } from "../../../../theme/tokens";
import { canReceiveSms } from "../../../../utils/formatters";
import { useHiringEligibleStaff } from "../hooks/useHiringEligibleStaff";
import type { GridDates, HiringGridCoord, HiringRecommendation } from "../types";
import { formatCoordLabel } from "../utils/coordLabels";
import { cardHasRequest, isRequested } from "../utils/moveRequests";
import OpportunityCard from "./OpportunityCard";

/**
 * How many candidates a COLLAPSED card shows — enough to choose from without the card becoming a list.
 * The rest are one click away; see `OpportunityCard`'s `onToggleMore`.
 */
const COLLAPSED_STAFF_ROWS = 3;

type Props = {
	rec: HiringRecommendation;
	/** Coordinate-derived identity, so a stored request survives the backend regenerating the list. */
	recKey: string;
	weekNumbers: number[];
	/** REQUIRED: the card names a calendar day, so a missing wiring must fail the build. */
	dates: GridDates;
	weekStartDay: number;
	jobDisplayName: string;
	/** Every `{recKey}::{nurseId}` the user has asked for, across all cards. */
	requested: readonly string[];
	onHoverChange: (coords: HiringGridCoord[]) => void;
	onRequestMove: (rec: HiringRecommendation, nurseId: number) => void;
	/** Un-mark a row. State only — see the `Requested` chip. */
	onUnrequestMove: (rec: HiringRecommendation, nurseId: number) => void;
	/**
	 * Reports that this move has no eligible staff, so the card will render nothing.
	 *
	 * The card owns the only query that can answer it, so the section above cannot count its own
	 * renderable cards without duplicating every fetch. It calls back instead.
	 */
	onNoEligibleStaff: (recKey: string) => void;
};

/**
 * One overstaffed shift paired with an understaffed one, and the people who could cover the move.
 *
 * The eligible staff arrive in the backend's own order, which ranks the LEAST senior first — the same
 * order the shipped move dialog rendered, kept deliberately so the person the system recommends doesn't
 * change identity between the two surfaces. Only the first is badged `Recommended`, and only while
 * nobody on the card has been asked yet: once a request is out, the card's job is to show what was
 * asked, not to keep recommending.
 *
 * Nothing sends silently — `Request Move` hands off to the broadcast composer with the message
 * prefilled, exactly as the dialog did.
 */
const MoveOpportunityCard: FC<Props> = ({
	rec,
	recKey,
	weekNumbers,
	dates,
	weekStartDay,
	jobDisplayName,
	requested,
	onHoverChange,
	onRequestMove,
	onUnrequestMove,
	onNoEligibleStaff,
}) => {
	const { t } = useTranslation();
	const { getNurseById } = useHospitalData();
	const formatPersonName = useFormatPersonName();
	// One object, memoised, so the two head labels cannot be built from different inputs.
	const coordContext = useMemo(() => ({ weekNumbers, dates, weekStartDay }), [weekNumbers, dates, weekStartDay]);
	const { data, isLoading, isError } = useHiringEligibleStaff({ rec, enabled: true });

	const fromSlot = rec.fromSlot;
	const toSlot = rec.toSlot;
	/**
	 * Only candidates we can actually message.
	 *
	 * `Request Move` opens the broadcast composer with one locked recipient, and the composer refuses to
	 * select anyone without a `userId` AND a `phoneNumber` — so listing such a person offered a button
	 * that could only ever open a composer with 0 recipients and a disabled Send. `canReceiveSms` is the
	 * composer's own rule, shared rather than restated, so the two cannot drift apart.
	 *
	 * Filtered before the slice, so the caption's count and the "+N more" line agree with the rows.
	 */
	// Everyone the backend ranked, in its order. Someone we cannot text still belongs on the list — they
	// ARE eligible to cover the move, and knowing who they are is the point. What they don't get is a
	// button that can only fail; see the row's `action` below.
	const staff = data?.staff ?? [];
	// Collapsed by default so a board of cards stays scannable; expanded in place, because the point of
	// naming the remaining people is to be able to pick one of them.
	const [expanded, setExpanded] = useState(false);
	const shown = expanded ? staff : staff.slice(0, COLLAPSED_STAFF_ROWS);
	const anyRequested = cardHasRequest(requested, recKey);

	const affectedCoords = [fromSlot, toSlot].filter((coord): coord is HiringGridCoord => Boolean(coord));

	// Told to the section in an effect, not during render: a parent cannot be updated while a child is
	// rendering. Same gate as the early return below, so the two can never disagree.
	const hasNoEligibleStaff = Boolean(data) && staff.length === 0;
	useEffect(() => {
		if (hasNoEligibleStaff) {
			onNoEligibleStaff(recKey);
		}
	}, [hasNoEligibleStaff, recKey, onNoEligibleStaff]);

	/**
	 * A move nobody can cover is not an opportunity, so the card does not render at all.
	 *
	 * Gated on the fetch having SUCCEEDED: while it is in flight, or when it failed, the card still
	 * renders its loading / error state — "we don't know yet" and "we couldn't ask" are not the same
	 * claim as "nobody can cover it", and silently dropping the card for either would hide a gap.
	 *
	 * Every hook above this line runs unconditionally, so the early return is safe.
	 */
	if (hasNoEligibleStaff) {
		return null;
	}

	return (
		<OpportunityCard
			tone={anyRequested ? "success" : "accent"}
			head={
				<Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75, minWidth: 0 }}>
					<SwapHorizIcon sx={{ fontSize: 16, color: INK.DISABLED, flexShrink: 0 }} />
					<Typography component="span" sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 700, color: INK.PRIMARY }}>
						{fromSlot ? formatCoordLabel(fromSlot, coordContext, t) : ""}
					</Typography>
					<ArrowRightAltIcon sx={{ fontSize: 20, color: ACCENT_BLUE.MAIN, flexShrink: 0 }} />
					<Typography component="span" sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 700, color: ACCENT_BLUE.INK }}>
						{toSlot ? formatCoordLabel(toSlot, coordContext, t) : ""}
					</Typography>
				</Box>
			}
			headEnd={
				anyRequested ? (
					<MetaChip label={t("hiring.opportunity.requested")} tone="success" icon={<CheckIcon />} dataTestId={`hiring-move-card-requested-${recKey}`} />
				) : undefined
			}
			caption={
				// With nobody eligible the empty line already says so; a "0 staff" caption just repeats it.
				data && fromSlot && staff.length > 0
					? jobDisplayName
						? t("hiring.opportunity.staffOnShift", { count: staff.length, role: jobDisplayName, shift: fromSlot.shiftLabel })
						: // The department catalog can be missing the job's display name; the caption still has to read.
							t("hiring.opportunity.staffOnShiftNoRole", { count: staff.length, shift: fromSlot.shiftLabel })
					: undefined
			}
			isLoading={isLoading}
			isError={isError}
			errorLabel={t("hiring.move.fetchError")}
			// Unreachable for this card — the early return above catches an empty list first. Kept because
			// the shared card requires a string, and it is the correct one should that gate ever move.
			emptyLabel={t("hiring.move.empty")}
			rowCount={shown.length}
			moreCount={Math.max(0, staff.length - shown.length)}
			onToggleMore={staff.length > COLLAPSED_STAFF_ROWS ? () => setExpanded((open) => !open) : undefined}
			isExpanded={expanded}
			onHoverChange={(hovered) => onHoverChange(hovered ? affectedCoords : [])}
			dataTestId={`hiring-move-card-${recKey}`}
		>
			{shown.map((candidate, index) => {
				const nurseId = candidate.nurseId;
				const nurse = getNurseById(nurseId);
				// One name for the row and for the disabled button's reason, so they can never disagree.
				// DEFAULT: a candidate missing from the roster has no name; the reason then reads unnamed.
				const nurseName = nurse ? formatPersonName(nurse) : "";
				const reachable = canReceiveSms(nurse);
				const done = isRequested(requested, recKey, nurseId);
				return (
					<PersonActionRow
						key={nurseId}
						dense
						index={index}
						name={nurseName}
						tone={done ? "success" : index === 0 ? "accent" : "neutral"}
						accentColor={ACCENT_BLUE.MAIN}
						badges={index === 0 && !anyRequested ? <MetaChip label={t("hiring.opportunity.recommended")} tone="primary" /> : undefined}
						action={
							done ? (
								// Clicking it un-marks the row and does NOTHING else — no composer, no message. The
								// broadcast has already left the app, so this cannot un-ask anyone; it corrects a row
								// that is marked when it shouldn't be (a mis-click, or a message that never landed).
								<MetaChip
									label={t("hiring.opportunity.requested")}
									tone="success"
									icon={<CheckIcon />}
									onClick={() => onUnrequestMove(rec, nurseId)}
									ariaLabel={t("hiring.move.unrequest")}
									tooltip={t("hiring.move.unrequest")}
									dataTestId={`hiring-move-requested-${nurseId}`}
								/>
							) : (
								<SystemButton
									buttonVariant="primary"
									size="compact"
									noFullWidthMobile
									/**
									 * The composer refuses to select anyone without a `userId` AND a `phoneNumber`
									 * (`useBroadcastMessageState`), so for them this button could only ever open a
									 * composer with 0 recipients and a disabled Send. Disabled with a reason beats both
									 * a dead-end button and dropping the person from a list they are genuinely eligible
									 * for. `canReceiveSms` is the composer's own rule, shared so the two cannot drift.
									 */
									disabled={!reachable}
									/**
									 * Names the person. A card lists three candidates and greys out whichever of them
									 * cannot be reached, so "no phone number on file" alone left you working out WHICH
									 * row the tooltip belonged to — and the name is also what you need in order to go
									 * and fix it. Falls back to the unnamed wording only when the department roster has
									 * no entry for the candidate at all, where there is no name to state.
									 */
									tooltip={reachable ? undefined : nurseName ? t("hiring.move.noPhoneTooltipNamed", { name: nurseName }) : t("hiring.move.noPhoneTooltip")}
									onClick={() => onRequestMove(rec, nurseId)}
									/**
									 * Same fixed accent as Apply — both are the solid primary action on their card.
									 *
									 * The disabled state has to be stated explicitly: an unconditional `backgroundColor`
									 * beats MUI's own disabled fill, so the button rendered the accent at reduced opacity
									 * — a muted purple that read as a THEME colour rather than as "not available".
									 * `action.disabledBackground` / `action.disabled` are the palette's own disabled
									 * pair, so this greys out exactly like every other disabled button in the app.
									 */
									sx={(theme) => ({
										backgroundColor: ACCENT_BLUE.MAIN,
										color: ACCENT_BLUE.ON_ACCENT,
										"&:hover": { backgroundColor: ACCENT_BLUE.DARK },
										"&.Mui-disabled": {
											backgroundColor: theme.palette.action.disabledBackground,
											color: theme.palette.action.disabled,
										},
									})}
									data-testid={`hiring-move-request-${nurseId}`}
								>
									{t("hiring.move.request")}
								</SystemButton>
							)
						}
						dataTestId={`hiring-move-row-${nurseId}`}
					/>
				);
			})}
		</OpportunityCard>
	);
};

export default MoveOpportunityCard;
